import { z } from 'zod';
// Mag follows up on what she asked the founder to do (s160).
//
// Source-of-truth zod schemas for:
//   • the completion-signal discriminated union persisted on each row
//   • a wire-shaped MagFollowup envelope the api hands the dashboard
//   • the "nextScan" decision the campaign-insight agent returns now,
//     which carries Mag's chosen interval + her reasoning sentence.
/** Completion signal — how the detector decides "this is shipped" for
 *  an open followup. Discriminated on `kind`; the detector branches
 *  per-variant. */
export const magFollowupCompletionSignalSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('event'),
        /** Lowercase analytics event name (e.g. `wizard_started`). */
        name: z.string().min(1).max(120),
        /** Property whose presence (non-null) on the event marks done.
         *  Omit when firing the event at all is the signal. */
        property: z.string().min(1).max(120).optional(),
        /** Optional exact value match alongside `property`. */
        value: z.string().min(1).max(200).optional(),
        /** How recent the match must be. Default 48h. Clamped [6, 720]. */
        within_hours: z.number().int().min(6).max(720).optional(),
    }),
    z.object({
        /** "Paid platform conversions arrived on this campaign." Used for
         *  conversion-tracking commitments on imported Google / Meta
         *  campaigns — the detector reads the campaign's latest snapshot
         *  metrics and closes when `metrics.conversions >= min_conversions`
         *  on a snapshot REFRESHED after the followup was created (so
         *  pre-existing conversions don't auto-close a still-open ask). */
        kind: z.literal('paid_conversions'),
        /** Minimum conversion count on the campaign's latest snapshot to
         *  count as "shipped". Default 1 — any conversion proves the
         *  pixel works. */
        min_conversions: z.number().int().min(1).max(1000).optional(),
    }),
    z.object({
        /** Mag couldn't auto-detect — only the founder can close this one. */
        kind: z.literal('manual'),
    }),
]);
/** Status states. Detector + founder + nudger all write here. */
export const magFollowupStatusSchema = z.enum(['open', 'done', 'abandoned']);
/** Who closed the followup. Null while open. */
export const magFollowupCompletedBySchema = z.enum(['auto', 'founder', 'mag']);
/** Wire shape the api returns. Mirrors the DB row 1:1 with ISO strings
 *  in place of timestamps so it survives JSON. */
export const magFollowupSchema = z.object({
    id: z.string().uuid(),
    brandId: z.string().uuid(),
    campaignId: z.string().uuid().nullable(),
    insightId: z.string().uuid().nullable(),
    summary: z.string().min(1).max(280),
    body: z.string().min(1).max(8000),
    completionSignal: magFollowupCompletionSignalSchema,
    status: magFollowupStatusSchema,
    nudgeCadenceDays: z.number().int().min(1).max(30),
    lastNudgedAt: z.string().datetime().nullable(),
    nudgeCount: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().nullable(),
    completedBy: magFollowupCompletedBySchema.nullable(),
    abandonedReason: z.string().max(800).nullable(),
    /** LLM cost (USD) of the extractor call that produced this row.
     *  0 for pre-s171 rows or when extraction was skipped. */
    extractionCostUsd: z.number().nonnegative(),
});
/** Mag's per-scan cadence decision (s160). The campaign-insight agent
 *  emits this alongside the insight itself; the server clamps and
 *  stamps it onto the campaign row (nextScanAt = now + intervalHours,
 *  nextScanReason = reason). Rendered verbatim on the campaign page +
 *  /home insight cards. */
export const nextScanDecisionSchema = z.object({
    /** How long until Mag's next read. Clamped [6, 504] = 6h .. 21d. */
    intervalHours: z.number().int().min(6).max(504),
    /** One-sentence mag-voice explanation. Surfaces verbatim. */
    reason: z.string().min(1).max(280),
});
/** Anomaly hint set on a campaign by the anomaly watcher (s177) when
 *  it triggers an off-cycle scan. The scan agent reads this when
 *  composing its read so the framing matches what the watcher saw
 *  ("noticed a 4x spike on wizard_started in the last hour"), then
 *  clears it after persisting. Null when no anomaly is pending — the
 *  common case.
 *
 *  s198 — extended to also cover paid-ad anomalies (CTR drops, CPM
 *  spikes, status flips). The shape is a soft discriminated union:
 *  legacy event-shaped hints (event_name + counts) coexist with new
 *  paid-ad hints (the paid_ad block). The kind enum maps both onto
 *  the four high-level categories the renderer + agent already
 *  understand — drop / spike / shift / funnel_break — so most
 *  consumer code doesn't need to branch. */
export const campaignAnomalyHintSchema = z.object({
    /** Coarse classification of what moved. The watcher's LLM judge
     *  picks one; the scan agent uses it to shape the opening line.
     *  Paid-ad CTR drops map to 'drop'; CPM spikes map to 'spike';
     *  status flips map to 'shift'. */
    kind: z.enum(['spike', 'drop', 'shift', 'funnel_break']),
    /** Lowercase analytics event name the anomaly is anchored on.
     *  Optional now (s198) — paid-ad anomalies set the paid_ad block
     *  instead. */
    event_name: z.string().min(1).max(200).optional(),
    /** Event count in the last hour at the time of detection.
     *  Optional now (s198) — set only for analytics-event anomalies. */
    current_count: z.number().int().nonnegative().optional(),
    /** Trailing 23h average per hour (the implicit baseline). Optional
     *  now (s198) — set only for analytics-event anomalies. */
    baseline_per_hour: z.number().nonnegative().optional(),
    /** s198 — paid-ad anomaly context. When set, this hint was
     *  triggered by a launched meta_paid_ad's metrics movement, not by
     *  analytics-event volume. event_name / current_count /
     *  baseline_per_hour are absent in that case. */
    paid_ad: z
        .object({
        /** Mag's deliverable id (e.g. "paid-angle-1"). */
        deliverable_id: z.string().min(1).max(64),
        /** Mag's hook (ad copy). Lets the scan agent reference the ad
         *  by name in the read: "the wizard-test angle dropped from
         *  2.1% to 0.4% ctr overnight". */
        hook: z.string().min(1).max(280),
        /** Which metric moved. Maps 1:1 onto the watcher's gate. */
        metric: z.enum(['ctr', 'cpm', 'status']),
        /** Latest snapshot value: percent for ctr, USD for cpm, status
         *  string for status flips ('PAUSED', 'DELETED', etc). Stored as
         *  a string for uniformity; the agent parses or quotes as
         *  needed. */
        current_value: z.string().min(1).max(40),
        /** Previous snapshot value. Same shape as current_value. */
        previous_value: z.string().min(1).max(40),
        /** How material the move is: a multiplier (2.5 = 2.5x) for ctr
         *  + cpm, 0 for status flips. */
        multiplier: z.number().nonnegative(),
    })
        .optional(),
    /** ISO-8601 timestamp the watcher made the decision. Lets the scan
     *  agent compute elapsed time if it wants ("about an hour ago"). */
    observed_at: z.string().min(1).max(40),
    /** The judge's internal reasoning. Persisted for telemetry / debug;
     *  NOT shown to the founder verbatim (the scan agent rewrites in
     *  mag voice). */
    judge_reason: z.string().min(1).max(800),
});
//# sourceMappingURL=mag-followup.js.map